/**
 * Voice API Key Manager
 * 
 * Manages multiple API keys for GROQ/OpenAI and Deepgram with automatic fallback.
 * If a request fails with one key, it silently retries with the next key without
 * showing any error to the user until ALL keys have been exhausted.
 */

// GROQ API Keys (for GPT-OSS model)
const GROQ_API_KEYS = [
    "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",
    // Add more GROQ keys here as needed
];

// Deepgram TTS API Keys
const DEEPGRAM_TTS_API_KEYS = [
    "f8993070fd9ae817f58d664dc34d2daa1e4efe0e",
    "1567445f5571fc0090253698bc4273d20b2214b1",
    "ac7c0b97fbec2b233d01e234bf643bd3200cb3fd",
    // Add more Deepgram keys here as needed
];

// Deepgram Transcription API Keys (for STT)
const DEEPGRAM_STT_API_KEYS = [
    "f8993070fd9ae817f58d664dc34d2daa1e4efe0e",
    "1567445f5571fc0090253698bc4273d20b2214b1",
    "ac7c0b97fbec2b233d01e234bf643bd3200cb3fd",
    // Add more Deepgram keys here as needed
];

// Track current key indices
let currentGroqKeyIndex = 0;
let currentDeepgramTtsKeyIndex = 0;
let currentDeepgramSttKeyIndex = 0;

/**
 * Get the next GROQ API key, cycling through the list
 */
export function getNextGroqKey(): string {
    const key = GROQ_API_KEYS[currentGroqKeyIndex];
    currentGroqKeyIndex = (currentGroqKeyIndex + 1) % GROQ_API_KEYS.length;
    return key;
}

/**
 * Get the next Deepgram TTS API key, cycling through the list
 */
export function getNextDeepgramTtsKey(): string {
    const key = DEEPGRAM_TTS_API_KEYS[currentDeepgramTtsKeyIndex];
    currentDeepgramTtsKeyIndex = (currentDeepgramTtsKeyIndex + 1) % DEEPGRAM_TTS_API_KEYS.length;
    return key;
}

/**
 * Get the next Deepgram STT API key, cycling through the list
 */
export function getNextDeepgramSttKey(): string {
    const key = DEEPGRAM_STT_API_KEYS[currentDeepgramSttKeyIndex];
    currentDeepgramSttKeyIndex = (currentDeepgramSttKeyIndex + 1) % DEEPGRAM_STT_API_KEYS.length;
    return key;
}

/**
 * Get the current Deepgram STT API key without advancing
 */
export function getCurrentDeepgramSttKey(): string {
    return DEEPGRAM_STT_API_KEYS[currentDeepgramSttKeyIndex];
}

/**
 * Advance to next Deepgram STT key (call this on failure)
 */
export function advanceDeepgramSttKey(): void {
    currentDeepgramSttKeyIndex = (currentDeepgramSttKeyIndex + 1) % DEEPGRAM_STT_API_KEYS.length;
}

/**
 * Check if we've tried all Deepgram STT keys
 */
export function hasMoreDeepgramSttKeys(triedCount: number): boolean {
    return triedCount < DEEPGRAM_STT_API_KEYS.length;
}

export const DEEPGRAM_STT_KEY_COUNT = DEEPGRAM_STT_API_KEYS.length;

interface FetchWithFallbackOptions {
    url: string;
    options: RequestInit;
    keyType: 'groq' | 'deepgram-tts';
    getAuthHeader: (key: string) => Record<string, string>;
}

/**
 * Execute a fetch request with automatic key fallback.
 * Silently retries with the next key if the request fails.
 * Only throws an error if ALL keys have been exhausted.
 */
async function fetchWithFallback(params: FetchWithFallbackOptions): Promise<Response> {
    const { url, options, keyType, getAuthHeader } = params;
    const keys = keyType === 'groq' ? GROQ_API_KEYS : DEEPGRAM_TTS_API_KEYS;

    let lastError: Error | null = null;

    for (let i = 0; i < keys.length; i++) {
        const key = keyType === 'groq' ? getNextGroqKey() : getNextDeepgramTtsKey();

        try {
            const authHeaders = getAuthHeader(key);
            const mergedHeaders = {
                ...options.headers,
                ...authHeaders,
            };

            const response = await fetch(url, {
                ...options,
                headers: mergedHeaders,
            });

            // Check for auth/rate limit errors that indicate we should try another key
            if (response.status === 401 || response.status === 403 || response.status === 429) {
                // Try next key
                continue;
            }

            // For other errors, still return the response for the caller to handle
            return response;
        } catch (error) {
            // Network error or similar - try next key
            lastError = error as Error;
            continue;
        }
    }

    // All keys exhausted
    throw lastError || new Error(`All ${keyType} API keys exhausted`);
}

/**
 * Make a GROQ API request with automatic key fallback.
 * Returns the Response object directly.
 */
export async function groqFetch(
    url: string,
    options: RequestInit
): Promise<Response> {
    return fetchWithFallback({
        url,
        options,
        keyType: 'groq',
        getAuthHeader: (key) => ({ Authorization: `Bearer ${key}` }),
    });
}

/**
 * Make a Deepgram TTS API request with automatic key fallback.
 * Returns the Response object directly.
 */
export async function deepgramTtsFetch(
    url: string,
    options: RequestInit
): Promise<Response> {
    return fetchWithFallback({
        url,
        options,
        keyType: 'deepgram-tts',
        getAuthHeader: (key) => ({ Authorization: `Token ${key}` }),
    });
}
